import { Component, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Observable } from "rxjs";
import { finalize } from "rxjs/operators";
import { AuthService } from "../auth.service";
import { environment } from "src/environments/environment";
import { ApiService, CreateAccountParams } from "../../http/api.service";
import { Account } from "gen/stackprint-model/account";

@Component({
  selector: "app-signup",
  templateUrl: "./signup.component.html",
  styleUrls: ["./signup.component.scss"],
})
export class SignupComponent implements OnInit {
  public step: "signup" | "verify" | "redirect" = "signup";
  public authCheck$: Observable<boolean>;
  public signupForm: FormGroup;
  public verifyEmailForm: FormGroup;
  public error = false;
  public busy = false;
  public account: Account;
  public termsUrl: string = environment.general.termsUrl;
  public referralCode: string;
  public inviteCode: string;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private apiService: ApiService,
    private activatedRoute: ActivatedRoute,
  ) {
    const passwordPattern =
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[@#$%^&+=!*])(?!.*\s).{8,}$/;

    this.signupForm = this.fb.group(
      {
        email: [undefined, [Validators.required, Validators.email]],
        password: [
          undefined,
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(passwordPattern),
          ],
        ],
        terms: [false, [Validators.requiredTrue]],
      },
      { updateOn: "blur" },
    );

    this.verifyEmailForm = this.fb.group(
      {
        code: ["", [Validators.required]],
      },
      { updateOn: "blur" },
    );
  }

  ngOnInit(): void {
    this.authCheck$ = this.authService.isAuthenticated();
    this.authCheck$.subscribe((authenticated) => {
      if (authenticated) {
        this.router.navigate(["/"]);
      }
    });

    this.activatedRoute.queryParams.subscribe((p) => {
      this.referralCode = p["r"];
      this.inviteCode = p["invite_code"];
    });

    // check if there was a pending signup
    const pendingAccountString = localStorage.getItem("signup.account");
    if (pendingAccountString) {
      this.account = JSON.parse(pendingAccountString);
      this.step = "verify";
    }
  }

  // workaround for safari not setting checkbox value automatically
  public toggleTerms(event: any) {
    this.signupForm.patchValue({
      terms: event.target.checked,
    });
  }

  public createAccount(): Observable<Account> {
    const params = this.signupForm.value as CreateAccountParams;
    if (this.referralCode) {
      params.referral_code = this.referralCode;
    }
    if (this.inviteCode) {
      params.invite_code = this.inviteCode;
    }
    params.referral_code = this.referralCode;
    return this.apiService.createAccount(params);
  }

  public signup() {
    this.error = false;

    if (!this.signupForm.valid) {
      this.signupForm.markAllAsTouched();
      return;
    }

    // ensure email is all lowercase
    this.signupForm.patchValue({
      email: (this.signupForm.value.email as string).toLowerCase(),
    });

    // create account
    this.busy = true;
    this.createAccount()
      .pipe(finalize(() => (this.busy = false)))
      .subscribe(
        (account) => {
          this.account = account;
          localStorage.setItem("signup.account", JSON.stringify(account));
          this.step = "verify";
        },
        () => {
          this.error = true;
        },
      );
  }

  public verifyEmail() {
    this.error = false;

    if (!this.verifyEmailForm.valid) {
      this.verifyEmailForm.markAllAsTouched();
      return;
    }
    this.busy = true;
    this.authService
      .verifyEmail(this.account.user_id, this.code.value)
      .pipe(finalize(() => (this.busy = false)))
      .subscribe({
        complete: () => {
          this.step = "redirect";
          localStorage.removeItem("signup.account");
          setTimeout(() => this.router.navigate(["/"]), 3000);
        },
        error: () => {
          this.error = true;
        },
      });
  }

  public resendConfirmationEmail() {
    this.busy = true;
    this.authService.resendConfirmationEmail(this.account.user_id).subscribe(
      () => (this.busy = false),
      () => (this.error = true),
    );
  }

  public cancelSignup() {
    this.error = false;
    this.busy = false;
    localStorage.removeItem("signup.account");
    this.account = undefined;
    this.verifyEmailForm.reset();
    this.signupForm.reset({ terms: false });
    this.step = "signup";
  }

  public get email() {
    return this.signupForm.get("email") as FormControl;
  }

  public get password() {
    return this.signupForm.get("password") as FormControl;
  }

  public get terms() {
    return this.signupForm.get("terms") as FormControl;
  }

  public get code() {
    return this.verifyEmailForm.get("code") as FormControl;
  }
}
